import React, { useState, useEffect } from 'react';
import { Logo } from './Logo';
import { useApp } from '../context/AppContext';
import { goToSection } from '../lib/sectionNav';
import { LockKeyhole } from 'lucide-react';

/**
 * App-style top bar.
 *
 * The page is a single funnel, so there is nothing to navigate to — the bar
 * carries the logo, the reading progress, the one button that matters and a
 * quiet way in to the console.
 */
export const Header = () => {
  const { navigate, route } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      // How far through the page you are. On a long single-column phone page
      // this is the only cue that there is an end in sight.
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const goHome = (event) => {
    event.preventDefault();
    if (route !== '/') navigate('/');
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-shadow duration-300 ${
        scrolled
          ? 'frost border-b border-ink-200/80 shadow-sm'
          : 'bg-[#fffdfb] border-b border-transparent'
      }`}
    >
      {/* Reading progress, pinned to the header's bottom edge. */}
      <span
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-brand-600 via-brand-500 to-gold-400 rounded-r-full transition-[width] duration-150 ease-out"
        style={{ width: `${progress * 100}%` }}
        aria-hidden="true"
      />

      <div className="shell h-[4.25rem] flex items-center justify-between gap-3">
        <a href="/" onClick={goHome} className="shrink-0 min-w-0" aria-label="Eduspire home">
          <Logo className="h-9" compact />
        </a>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => goToSection('#enroll', route, navigate)}
            className="btn btn-primary btn-sm"
          >
            Book seat
          </button>

          {/* Operator entry to the console. Deliberately quiet — this is a
              marketing page — but on the first screen rather than only at the
              bottom of a very long scroll. Navigating rather than opening the
              modal directly keeps /admin bookmarkable. */}
          <button
            onClick={() => navigate('/admin')}
            aria-label="Admin console"
            title="Admin console"
            className="p-2.5 -mr-2 rounded-xl text-ink-400 hover:text-ink-800 hover:bg-ink-100 transition-colors"
          >
            <LockKeyhole className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
